"use client";

// Ported verbatim from pf-platform hooks/use-visual-viewport-box.ts @553406bc8 (LANE-mobile-keyboard-pattern-everywhere R8, 2026-10-29). Keep the two copies identical.

/**
 * useVisualViewportBox — the box of the screen the reader can actually see.
 *
 * 2026-10-01 (Lars, iPhone): with the keyboard open, iOS Safari shrinks the
 * VISUAL viewport but leaves the layout viewport (window.innerHeight, 100dvh,
 * `position: fixed; bottom: 0`) where it was. Anything sized or placed from the
 * layout viewport therefore ends up partly behind the keyboard, or above the
 * top of the screen once iOS pans the page to show the caret. That is how the
 * KLIPY search row slid out of view and how the post pop-up kept a gap.
 *
 * A `position: fixed` element is laid out against the LAYOUT viewport, so the
 * visible area inside it is exactly:
 *     top    = visualViewport.offsetTop
 *     left   = visualViewport.offsetLeft
 *     width  = visualViewport.width
 *     height = visualViewport.height
 * This hook reports that box and keeps it current on visualViewport `resize`
 * (keyboard, URL bar) and `scroll` (iOS panning to the caret).
 *
 * Same listener pattern as post-composer.tsx's upload-dialog placer and
 * use-anchored-position.ts: listener bursts are coalesced behind one rAF per
 * frame, and a frame where the reader is pinch-zoomed (scale !== 1) keeps the
 * last box rather than chasing the zoom.
 *
 * `keyboardOpen` (2026-10-28, Lars's recording 19:00 — "it starts to shake"):
 * the old test (innerHeight − visible height > 120px) flipped in the middle of
 * a drag while iOS panned, so the pop-up jumped between card and full screen.
 * It is now a small state machine, never reading innerHeight:
 *   rest  = the tallest visible height seen while the keyboard is down (a
 *           width change resets it; with the keyboard up it is forgotten and
 *           the state is held until the field loses focus).
 *   ON    = an editable field has focus AND rest − height > 120px.
 *   OFF   = the field loses focus, OR rest − height ≤ 120px for 300ms.
 *   Nothing flips while a finger is on the screen; it is settled on touchend.
 * While a finger is down, `top` / `left` are also held (width and height pass
 * through), so the pop-up does not chase iOS's page pan a frame late.
 *
 * One module store for every subscriber: the listeners attach once
 * (refcounted); each subscriber remembers the snapshot it was last told about.
 *
 * Returns null while `active` is false or where visualViewport is missing.
 */

import { useCallback, useEffect, useSyncExternalStore, type RefObject } from "react";

export interface VisualViewportBox {
  top: number;
  left: number;
  width: number;
  height: number;
  keyboardOpen: boolean;
}

const KEYBOARD_THRESHOLD_PX = 120;
const KEYBOARD_OFF_SETTLE_MS = 300;

function sameBox(a: VisualViewportBox | null, b: VisualViewportBox | null) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height &&
    a.keyboardOpen === b.keyboardOpen
  );
}

const TEXT_INPUT_TYPES = new Set([
  "", "text", "search", "email", "url", "tel", "password", "number",
  "date", "datetime-local", "month", "time", "week",
]);

function isEditable(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) {
    return TEXT_INPUT_TYPES.has((el.getAttribute("type") ?? "").toLowerCase()) && !el.readOnly && !el.disabled;
  }
  return el instanceof HTMLElement && el.isContentEditable;
}

/* ── the store ─────────────────────────────────────────────────────────────── */

interface Subscriber {
  seen: VisualViewportBox | null;
  onChange: () => void;
}

const subscribers = new Set<Subscriber>();
let cachedBox: VisualViewportBox | null = null;
let kbOn = false;
let restHeight: number | null = null;
let lastWidth: number | null = null;
let lowSince: number | null = null; // keyboard on, but the height is back near rest since…
let offTimer: ReturnType<typeof setTimeout> | null = null;
let touchActive = false;
let raf: number | null = null;
let detach: (() => void) | null = null;

function clearOffTimer() {
  if (offTimer !== null) clearTimeout(offTimer);
  offTimer = null;
}

/** Read the visual viewport, advance the keyboard state, refresh the cache.
 *  Returns true when the cached box changed. */
function compute(): boolean {
  if (typeof window === "undefined") return false;
  const vv = window.visualViewport;
  if (!vv) {
    const changed = cachedBox !== null;
    cachedBox = null;
    return changed;
  }
  if (cachedBox && vv.scale > 1.01) return false; // pinch-zoomed: keep the last box
  const h = vv.height;
  const w = vv.width;

  if (lastWidth !== null && Math.abs(w - lastWidth) > 1) {
    restHeight = kbOn ? null : h; // rotation / resize: a new baseline
  }
  lastWidth = w;
  if (!kbOn) restHeight = restHeight === null ? h : Math.max(restHeight, h);

  const editable = isEditable(typeof document === "undefined" ? null : document.activeElement);
  let want = kbOn;
  if (!kbOn) {
    clearOffTimer();
    lowSince = null;
    want = editable && restHeight !== null && restHeight - h > KEYBOARD_THRESHOLD_PX;
  } else if (!editable) {
    want = false;
  } else if (restHeight !== null && restHeight - h <= KEYBOARD_THRESHOLD_PX) {
    const now = performance.now();
    if (lowSince === null) lowSince = now;
    if (now - lowSince >= KEYBOARD_OFF_SETTLE_MS) {
      want = false;
    } else if (offTimer === null) {
      offTimer = setTimeout(() => {
        offTimer = null;
        schedule();
      }, KEYBOARD_OFF_SETTLE_MS - (now - lowSince) + 5);
    }
  } else {
    lowSince = null;
    clearOffTimer();
  }
  // Never flip while a finger is on the screen; touchend settles it.
  if (!touchActive && want !== kbOn) {
    kbOn = want;
    lowSince = null;
    clearOffTimer();
    if (!kbOn) restHeight = restHeight === null ? h : Math.max(restHeight, h);
  }

  const hold = touchActive && cachedBox !== null;
  const next: VisualViewportBox = {
    top: hold ? cachedBox!.top : Math.max(0, vv.offsetTop),
    left: hold ? cachedBox!.left : Math.max(0, vv.offsetLeft),
    width: w,
    height: h,
    keyboardOpen: kbOn,
  };
  if (sameBox(cachedBox, next)) return false;
  cachedBox = next;
  return true;
}

function notify() {
  for (const s of subscribers) {
    if (s.seen !== cachedBox) {
      s.seen = cachedBox;
      s.onChange();
    }
  }
}

function schedule() {
  if (raf !== null) return;
  raf = requestAnimationFrame(() => {
    raf = null;
    compute();
    notify();
  });
}

function touchCount(e: Event): number {
  return (e as TouchEvent).touches?.length ?? 0;
}

function attach(): () => void {
  const vv = window.visualViewport;
  const onTouch = (e: Event) => {
    const was = touchActive;
    touchActive = touchCount(e) > 0;
    if (was && !touchActive) schedule();
  };
  const clearTouch = () => {
    if (!touchActive) return;
    touchActive = false;
    schedule();
  };
  const onVisibility = () => {
    if (document.visibilityState !== "visible") clearTouch();
  };
  const cap = { capture: true, passive: true } as const;
  vv?.addEventListener("resize", schedule);
  vv?.addEventListener("scroll", schedule);
  window.addEventListener("resize", schedule);
  window.addEventListener("orientationchange", schedule);
  document.addEventListener("touchstart", onTouch, cap);
  document.addEventListener("touchend", onTouch, cap);
  document.addEventListener("touchcancel", onTouch, cap);
  document.addEventListener("focusin", schedule, true);
  document.addEventListener("focusout", schedule, true);
  window.addEventListener("blur", clearTouch);
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    if (raf !== null) cancelAnimationFrame(raf);
    raf = null;
    clearOffTimer();
    vv?.removeEventListener("resize", schedule);
    vv?.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
    window.removeEventListener("orientationchange", schedule);
    document.removeEventListener("touchstart", onTouch, cap);
    document.removeEventListener("touchend", onTouch, cap);
    document.removeEventListener("touchcancel", onTouch, cap);
    document.removeEventListener("focusin", schedule, true);
    document.removeEventListener("focusout", schedule, true);
    window.removeEventListener("blur", clearTouch);
    document.removeEventListener("visibilitychange", onVisibility);
    touchActive = false;
  };
}

function subscribeBox(onChange: () => void): () => void {
  const sub: Subscriber = { seen: cachedBox, onChange };
  subscribers.add(sub);
  if (subscribers.size === 1) {
    if (!kbOn) restHeight = null; // a fresh baseline for this pop-up
    detach = attach();
  }
  // Catch anything that moved between first read and subscribe.
  compute();
  notify();
  return () => {
    subscribers.delete(sub);
    if (subscribers.size === 0 && detach) {
      detach();
      detach = null;
    }
  };
}

function getBoxSnapshot(): VisualViewportBox | null {
  // With listeners attached they keep the cache current; before the first
  // subscribe (first render) read it here. Stable while nothing changed.
  if (subscribers.size === 0) compute();
  return cachedBox;
}

const noopSubscribe = () => () => {};
const getNull = () => null;

export function useVisualViewportBox(active: boolean): VisualViewportBox | null {
  return useSyncExternalStore(
    active ? subscribeBox : noopSubscribe,
    active ? getBoxSnapshot : getNull,
    getNull,
  );
}

/**
 * useLockViewportPan — while a phone pop-up fits the visible area, a drag that
 * nothing under the finger can scroll would pan the page under the fixed
 * pop-up (Lars, 2026-10-28 19:00: the header / reply box / past the list end
 * moved the whole screen and the caret drifted). Lars approved stopping those
 * drags — and only those.
 *
 * Decided ONCE per gesture, on its first non-zero move. The drag is left alone
 * when any element from the finger up to `rootRef` can scroll that way (inside
 * the pop-up, or a picker it portals to <body>), for a pinch (2+ fingers), a
 * text selection, a first move ≥450ms after touchstart (the iOS loupe), or a
 * slider / video / audio / [data-allow-touchmove]. Otherwise every move of
 * that gesture is cancelled. Starts from 9508bce7's version (removed in
 * 8bea90b3 when it blocked scrolls Lars wanted); this one never blocks a drag
 * a scroll area can take.
 */
const LOUPE_HOLD_MS = 450;

export function useLockViewportPan(
  active: boolean,
  rootRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!active || typeof document === "undefined") return;
    let startX = 0;
    let startY = 0;
    let startT = 0;
    let decision: "allow" | "block" | null = null;
    const canScroll = (el: HTMLElement, dx: number, dy: number): boolean => {
      const cs = getComputedStyle(el);
      if (Math.abs(dy) >= Math.abs(dx)) {
        if (!/(auto|scroll|overlay)/.test(cs.overflowY)) return false;
        if (el.scrollHeight <= el.clientHeight + 1) return false;
        // Finger moving up (dy < 0) scrolls content down.
        if (dy < 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
        return el.scrollTop > 0;
      }
      if (!/(auto|scroll|overlay)/.test(cs.overflowX)) return false;
      if (el.scrollWidth <= el.clientWidth + 1) return false;
      if (dx < 0) return el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      return el.scrollLeft > 0;
    };
    const decide = (e: TouchEvent, dx: number, dy: number): "allow" | "block" => {
      if (e.touches.length > 1) return "allow";
      if (performance.now() - startT >= LOUPE_HOLD_MS) return "allow";
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('input[type="range"], video, audio, [role="slider"], [data-allow-touchmove]')) return "allow";
      const sel = typeof window.getSelection === "function" ? window.getSelection() : null;
      if (sel && !sel.isCollapsed) return "allow";
      const root = rootRef.current;
      for (let el: Element | null = target; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
        if (el instanceof HTMLElement && canScroll(el, dx, dy)) return "allow";
        if (el === root) break;
      }
      return "block";
    };
    const onStart = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        decision = "allow"; // a second finger: a pinch, never cancelled
        return;
      }
      decision = null;
      startX = e.touches[0]?.clientX ?? 0;
      startY = e.touches[0]?.clientY ?? 0;
      startT = performance.now();
    };
    const onMove = (e: TouchEvent) => {
      if (decision === null) {
        if (e.touches.length > 1) {
          decision = "allow";
          return;
        }
        const t = e.touches[0];
        if (!t) return;
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (dx === 0 && dy === 0) return;
        decision = decide(e, dx, dy);
      }
      if (decision === "block" && e.cancelable) e.preventDefault();
    };
    document.addEventListener("touchstart", onStart, { passive: true, capture: true });
    document.addEventListener("touchmove", onMove, { passive: false, capture: true });
    return () => {
      document.removeEventListener("touchstart", onStart, { capture: true });
      document.removeEventListener("touchmove", onMove, { capture: true });
    };
  }, [active, rootRef]);
}

/** Phone width — where the GIF picker becomes a sheet. Same breakpoint as the
 *  post pop-up's own isMobile flag and every ≤767px phone rule in globals.css. */
export const PHONE_SHEET_QUERY = "(max-width: 767px)";

/** Live `matchMedia` flag; false during SSR. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mq = window.matchMedia(query);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}
